import { type JSX, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Code,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  type ArvoreDoMenu,
  type Capacidades,
  type CampoDeTextoMenu,
  type DiferencasDoMenu,
  type EstadoDoMenu,
  type FaqDetalhe,
  type NoDoMenu,
  type OpcaoDoMenu,
  type ProblemaDoMenu,
  type VersaoNoHistorico,
  api,
  ehMudancaEstrutural,
  explicarMotivo,
} from '../lib/api.js';
import { CorpoWhatsapp } from './PreviaWhatsapp.js';
import { EditorFaq } from './EditorFaq.js';
import {
  ModalDesativarNo,
  ModalVisibilidade,
  ModalMoverNo,
  ModalNovaOpcao,
  ModalTrocarDestino,
} from './MenuEstrutura.js';
import { BlocoDeAjuda, Vazio } from './ui.js';
import {
  DESCRICAO_REGRA,
  MARCA_REGRA,
  MARCA_TODOS,
  ROTULO_TODOS,
  noDiferencia,
  type PerfilDaPrevia,
  ROTULO_REGRA,
  opcaoVisivel,
  regraInerte,
} from '../lib/visibilidade.js';

/**
 * Editor da árvore de atendimento, em duas colunas.
 *
 * ESTRUTURA À ESQUERDA, CONTEÚDO À DIREITA. A primeira versão desta tela
 * empilhava os nove nós numa página só: dava para ler o conteúdo e não dava
 * para ver a árvore — e árvore é justamente o que a etapa 2 vai mexer. O
 * arranjo é o mesmo de Conversas (lista à esquerda, item à direita), de
 * propósito: quem usa o painel já conhece.
 *
 * O que fica ACIMA das colunas é o que vale para a árvore inteira: a versão, o
 * alternador de horário, publicar e descartar. O que é de um nó fica na
 * direita.
 *
 * O ESCOPO DA EDIÇÃO não mudou com o layout: texto de nó, rótulo de opção e
 * ordem das opções. Criar, remover ou mover nó, e trocar `action`/`next`, são
 * etapa 2 — e o servidor recusa mesmo que a tela ofereça.
 *
 * TRÊS COISAS QUE A TELA TEM DE DIZER, porque o dado sozinho não diz:
 *
 *  1. PARA ONDE cada opção leva. Na esquerda isso é um símbolo com descrição
 *     no toque; na direita, texto corrido. Quem renomeia "Solicitar orçamento"
 *     precisa saber que aquela opção passa a conversa para a secretaria;
 *  2. que o `9` é do engine e não entra na numeração — e quantas vagas sobram,
 *     porque o teto é 8 e a raiz já está nele;
 *  3. onde há alteração não publicada, nó por nó: quem edita cinco nós
 *     precisa saber em quais mexeu antes de publicar.
 */

/** Valores de exemplo dos marcadores, para a prévia mostrar texto de verdade. */
const EXEMPLOS: Record<string, string> = {
  nome: 'Felipe',
  assunto: 'curso teórico',
  horario: 'Segunda a sexta, 08:00 às 18:00',
  proxima_abertura: 'amanhã a partir das 08:00',
};

const ROTULO_CAMPO: Record<CampoDeTextoMenu, string> = {
  texto: 'Texto do nó',
  textoSemAssunto: 'Texto quando não há assunto definido',
  saudacao: 'Saudação (quando sabemos o nome)',
  saudacaoSemNome: 'Saudação (quando não sabemos o nome)',
  textoForaHorario: 'Rodapé de fora do horário',
  textoProximaAbertura: 'Emenda da próxima abertura',
};

const COR_DESTINO: Record<OpcaoDoMenu['tipoDeDestino'], string> = {
  no: 'azul',
  transbordo: 'orange',
  faq_direta: 'teal',
  duvidas: 'violet',
  acao: 'gray',
};

/**
 * Símbolo do destino, para a coluna estreita.
 *
 * Símbolo sozinho não informa ninguém — cada um vem com `Tooltip` que diz a
 * frase inteira, e a direita mostra o texto sem depender de toque. O símbolo
 * serve para VARRER a árvore com os olhos e ver onde ela sai do menu.
 */
const SIMBOLO_DESTINO: Record<OpcaoDoMenu['tipoDeDestino'], string> = {
  no: '▸',
  transbordo: '⇥',
  faq_direta: '→',
  duvidas: '💬',
  acao: '⚙',
};

/** Troca os marcadores por exemplos, para a prévia não mostrar `{nome}`. */
function comExemplos(modelo: string): string {
  return modelo.replace(/\{([a-z_]+)\}/g, (literal, chave: string) => EXEMPLOS[chave] ?? literal);
}

interface Hierarquia {
  /** Nós na ordem da raiz às folhas, com a profundidade de cada um. */
  ordem: { no: NoDoMenu; nivel: number }[];
  /** Filhos de cada nó — só os que "nascem" ali. Ver a nota abaixo. */
  filhos: Map<string, string[]>;
  raiz: NoDoMenu | null;
}

/**
 * Monta a hierarquia da raiz às folhas.
 *
 * Nó alcançado por mais de um caminho (`teorico` vem da raiz e de `formacao`)
 * pertence ao caminho MAIS RASO e aparece uma vez só. Mostrar o mesmo nó duas
 * vezes daria duas caixas de edição para o mesmo texto — e salvar numa delas
 * faria a outra mentir. O vínculo extra não se perde: o nó mostra "aparece em:
 * root, formacao".
 */
function hierarquia(nos: readonly NoDoMenu[]): Hierarquia {
  const porId = new Map(nos.map((n) => [n.noId, n]));
  const visitados = new Set<string>();
  const ordem: { no: NoDoMenu; nivel: number }[] = [];
  const filhos = new Map<string, string[]>();

  const raiz = nos.find((n) => n.ehRaiz) ?? null;
  const fila: { id: string; nivel: number }[] = raiz ? [{ id: raiz.noId, nivel: 0 }] : [];

  while (fila.length > 0) {
    const atual = fila.shift()!;
    if (visitados.has(atual.id)) continue;
    const no = porId.get(atual.id);
    if (!no) continue;
    visitados.add(atual.id);
    ordem.push({ no, nivel: atual.nivel });

    const meus: string[] = [];
    for (const o of no.opcoes) {
      if (!o.proximoNo) continue;
      // Já visitado = pertence a um caminho mais raso; não vira filho aqui.
      if (visitados.has(o.proximoNo) || meus.includes(o.proximoNo)) continue;
      if (fila.some((f) => f.id === o.proximoNo)) continue;
      meus.push(o.proximoNo);
      fila.push({ id: o.proximoNo, nivel: atual.nivel + 1 });
    }
    filhos.set(no.noId, meus);
  }

  // Nó que nenhuma opção alcança (o de dúvida livre é empilhado por `action`)
  // entra no fim, no primeiro nível.
  for (const no of nos) {
    if (!visitados.has(no.noId)) ordem.push({ no, nivel: 0 });
  }
  return { ordem, filhos, raiz };
}

export function Menu({ capacidades }: { capacidades: Capacidades }): JSX.Element {
  const [estado, setEstado] = useState<EstadoDoMenu | null>(null);
  const [diferencas, setDiferencas] = useState<DiferencasDoMenu | null>(null);
  const [historico, setHistorico] = useState<VersaoNoHistorico[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  /** Qual árvore está na tela: a que está no ar ou a que está sendo escrita. */
  const [vendo, setVendo] = useState<'rascunho' | 'publicada'>('rascunho');
  const [horario, setHorario] = useState<'em' | 'fora'>('em');
  /**
   * Por quais olhos a prévia é montada.
   *
   * Com o menu por tipo ligado, a NUMERAÇÃO muda por pessoa: a opção 3 do
   * aluno pode ser a 2 do interessado. Sem este alternador, a prévia mostraria
   * sempre uma das visões e quem edita acharia que é a de todo mundo.
   */
  const [perfil, setPerfil] = useState<PerfilDaPrevia>('aluno');
  /** Nó aberto na direita. `null` só até a primeira carga: a raiz entra sozinha. */
  const [selecionado, setSelecionado] = useState<string | null>(null);
  /**
   * No celular é uma coluna por vez. `false` mostra a árvore; tocar num nó
   * abre a edição, e "Voltar" devolve a árvore. No desktop as duas colunas
   * aparecem sempre, então este estado não muda nada lá.
   */
  const [editandoNoCelular, setEditandoNoCelular] = useState(false);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [publicando, { open: abrirPublicar, close: fecharPublicar }] = useDisclosure(false);
  /**
   * A ajuda da tela está aberta?
   *
   * A coluna esquerda GRUDA no topo: com a ajuda aberta, o pé da coluna sairia
   * da janela, e numa coluna grudada o que passa da janela não volta com
   * rolagem. Então a árvore encurta enquanto a ajuda está aberta — ver o `mah`
   * do `ScrollArea`.
   */
  const [ajuda, setAjuda] = useState(false);
  /**
   * O menu por tipo está ligado no servidor?
   *
   * Vem da API, e não de uma constante da tela: o valor mora no ambiente do
   * worker e da api, e uma cópia no painel mentiria sobre o que o bot faz no
   * dia em que alguém ligasse lá e não aqui.
   */
  const [menuPorTipoAtivo, setMenuPorTipoAtivo] = useState(false);
  const [descartando, { open: abrirDescartar, close: fecharDescartar }] = useDisclosure(false);
  const [vendoHistorico, { open: abrirHistorico, close: fecharHistorico }] = useDisclosure(false);
  const [nota, setNota] = useState('');
  const [salvando, setSalvando] = useState(false);
  /**
   * FAQ aberta a partir de uma opção.
   *
   * `carregando` enquanto busca o detalhe: o atalho existe para o caso "esse
   * item responde errado, deixa eu arrumar", e abrir uma caixa vazia por meio
   * segundo seria pior que esperar com o aviso.
   */
  const [faqAberta, setFaqAberta] = useState<FaqDetalhe | null>(null);
  const [abrindoFaq, setAbrindoFaq] = useState(false);
  const [contextoDeFaq, setContextoDeFaq] = useState<{
    categorias: string[];
    categoriaPendenteConteudo: string;
  } | null>(null);

  /** Diálogo de estrutura aberto, se houver. */
  const [dialogo, setDialogo] = useState<
    | { tipo: 'nova_opcao'; noId: string }
    | { tipo: 'trocar_destino'; opcaoId: string; label: string; destinoAtual: string }
    | { tipo: 'mover'; noId: string }
    | { tipo: 'desativar'; noId: string }
    | {
        tipo: 'visibilidade';
        opcaoId: string;
        label: string;
        regraAtual: string | null;
      }
    | null
  >(null);

  const carregar = useCallback(async () => {
    try {
      const [e, d, h] = await Promise.all([
        api.get<EstadoDoMenu>('/admin/menu'),
        api.get<DiferencasDoMenu>('/admin/menu/diferencas'),
        api.get<{ itens: VersaoNoHistorico[] }>('/admin/menu/historico'),
      ]);
      setEstado(e);
      setDiferencas(d);
      setHistorico(h.itens);
      setErro(null);
    } catch (err) {
      setErro(explicarMotivo(err, 'falha ao carregar a árvore'));
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Uma vez: o interruptor é do ambiente e não muda com a navegação.
  useEffect(() => {
    void api
      .get<{ menuPorTipoAtivo: boolean }>('/admin/menu/visibilidade')
      .then((r) => setMenuPorTipoAtivo(r.menuPorTipoAtivo))
      .catch(() => undefined);
  }, []);

  const arvore: ArvoreDoMenu | null =
    estado === null
      ? null
      : vendo === 'rascunho' && estado.rascunho
        ? estado.rascunho
        : estado.publicada;

  const estrutura = useMemo(() => hierarquia(arvore?.nos ?? []), [arvore]);

  /**
   * A raiz entra selecionada e expandida assim que a árvore chega.
   *
   * A tela nunca fica vazia, e o primeiro nível fica à vista — abaixo dele
   * tudo começa recolhido, senão a rolagem enorme volta em outro formato.
   */
  useEffect(() => {
    if (!estrutura.raiz) return;
    setSelecionado((atual) => atual ?? estrutura.raiz!.noId);
    setExpandidos((atuais) => (atuais.size > 0 ? atuais : new Set([estrutura.raiz!.noId])));
  }, [estrutura.raiz]);

  /** Nós com alteração ainda não publicada. Vem do mesmo diff do Publicar. */
  const nosAlterados = useMemo(
    () => new Set((diferencas?.mudancas ?? []).map((m) => m.noId)),
    [diferencas],
  );

  /**
   * Problemas por nó.
   *
   * Vêm da MESMA validação que bloqueia a publicação. Marcados na árvore para a
   * pessoa achar sem ler uma lista — e listados no nó aberto, com o que fazer.
   */
  const problemasPorNo = useMemo(() => {
    const mapa = new Map<string, ProblemaDoMenu[]>();
    for (const p of estado?.problemas ?? []) {
      const lista = mapa.get(p.noId);
      if (lista) lista.push(p);
      else mapa.set(p.noId, [p]);
    }
    return mapa;
  }, [estado]);

  if (erro && !estado) {
    return (
      <Stack gap="sm" align="flex-start">
        <Alert color="red" variant="light" role="alert">
          {erro}
        </Alert>
        <Button onClick={() => void carregar()}>Tentar de novo</Button>
      </Stack>
    );
  }

  if (!estado || !arvore) {
    return (
      <Group gap="xs">
        <Loader size="sm" />
        <Text c="dimmed">Carregando…</Text>
      </Group>
    );
  }

  if (!estado.publicada) {
    return (
      <Vazio titulo="Nenhuma versão publicada.">
        A árvore vive no banco desde a migração, e o bot recusa subir sem uma versão publicada.
        Rode <Code>npm run menu:semear</Code> no servidor para criar a primeira a partir do código.
      </Vazio>
    );
  }

  const temRascunho = estado.rascunho !== null;

  /**
   * Quem pode editar, e quando.
   *
   * A regra é o PAPEL, não o status da versão na tela. Amarrar a edição a
   * "esta versão não é a publicada" criava um impasse: sem rascunho, a tela
   * mostra a versão publicada, tudo ficava em somente leitura — e o rascunho
   * que destravaria a edição só nasce de uma edição.
   *
   * O rascunho é criado pelo SERVIDOR na primeira escrita (`garantirRascunho`),
   * e o id de opção que a tela manda nessa primeira vez é o da versão
   * publicada — traduzido por `(nó, ordem)` do outro lado. Então a tela não
   * precisa de botão "criar rascunho": basta deixar editar, que é o que o
   * cabeçalho sempre prometeu.
   *
   * A exceção é olhar a versão NO AR tendo rascunho aberto: ali a tela é
   * comparação, não superfície de edição, e o alternador é a saída.
   */
  const vendoOEditavel = !temRascunho || vendo === 'rascunho';
  const editavel = capacidades.editar && vendoOEditavel;
  const mudancas = diferencas?.mudancas ?? [];
  const noAberto = arvore.nos.find((n) => n.noId === selecionado) ?? null;

  async function salvar(acao: () => Promise<unknown>, mensagem: string): Promise<void> {
    setSalvando(true);
    try {
      await acao();
      notifications.show({ color: 'green', message: mensagem });
      await carregar();
      setVendo('rascunho');
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Não foi possível salvar',
        message: explicarMotivo(e, 'falha ao salvar'),
      });
    } finally {
      setSalvando(false);
    }
  }

  async function publicar(): Promise<void> {
    setSalvando(true);
    try {
      const r = await api.post<{ numero: number; mudancas: number }>('/admin/menu/publicar', {
        ...(nota.trim() ? { nota: nota.trim() } : {}),
      });
      fecharPublicar();
      setNota('');
      notifications.show({
        color: 'green',
        title: `Versão ${r.numero} publicada`,
        message: `${r.mudancas} mudança(s) no ar. O bot passa a usar a árvore nova em segundos.`,
      });
      await carregar();
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Não foi possível publicar',
        message: explicarMotivo(e, 'falha ao publicar'),
      });
    } finally {
      setSalvando(false);
    }
  }

  /**
   * Abre a FAQ no MESMO editor da aba FAQs.
   *
   * Escolhido em vez de uma prévia recolhível porque o caso de uso real é
   * corrigir: quem percebe que um item de menu responde errado quer arrumar a
   * resposta ali, não só conferir. O editor respeita o papel — `leitor` abre em
   * leitura — e já é um modal com guarda de alteração não salva.
   */
  async function abrirFaq(faqId: string): Promise<void> {
    setAbrindoFaq(true);
    try {
      // As categorias e a categoria de encaminhamento vêm da mesma rota que
      // alimenta a aba FAQs: o editor precisa das duas para o seletor e para o
      // aviso de `pendente_conteudo`.
      const [detalhe, lista] = await Promise.all([
        api.get<FaqDetalhe>(`/admin/faqs/${faqId}`),
        contextoDeFaq
          ? Promise.resolve(null)
          : api.get<{
              categorias: { categoria: string }[];
              categoriaPendenteConteudo: string;
            }>('/admin/faqs?limite=1'),
      ]);
      if (lista) {
        setContextoDeFaq({
          categorias: lista.categorias.map((c) => c.categoria),
          categoriaPendenteConteudo: lista.categoriaPendenteConteudo,
        });
      }
      setFaqAberta(detalhe);
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Não foi possível abrir a FAQ',
        message: explicarMotivo(e, 'falha ao carregar'),
      });
    } finally {
      setAbrindoFaq(false);
    }
  }

  function alternar(noId: string): void {
    setExpandidos((atuais) => {
      const novo = new Set(atuais);
      if (novo.has(noId)) novo.delete(noId);
      else novo.add(noId);
      return novo;
    });
  }

  return (
    <Stack gap="md">
      {/* ------------------------------------------------------------------
          Acima das colunas: o que vale para a árvore INTEIRA.
      ------------------------------------------------------------------ */}
      <Card withBorder padding="md">
        <Stack gap="sm">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Group gap="sm" wrap="wrap">
              <Text fw={600}>Versão {arvore.versao.numero}</Text>
              <Badge color={arvore.versao.status === 'publicada' ? 'teal' : 'orange'}>
                {arvore.versao.status === 'publicada' ? 'no ar' : 'rascunho'}
              </Badge>
              {arvore.versao.status === 'publicada' && arvore.versao.publicadaPorNome && (
                <Text size="xs" c="dimmed">
                  publicada por {arvore.versao.publicadaPorNome}
                  {arvore.versao.publicadaEm
                    ? ` em ${new Date(arvore.versao.publicadaEm).toLocaleString('pt-BR')}`
                    : ''}
                </Text>
              )}
              {arvore.versao.status === 'rascunho' && arvore.versao.alteradaPorNome && (
                <Text size="xs" c="dimmed">
                  alterado por {arvore.versao.alteradaPorNome}
                  {arvore.versao.alteradaEm
                    ? ` em ${new Date(arvore.versao.alteradaEm).toLocaleString('pt-BR')}`
                    : ''}
                </Text>
              )}
            </Group>

            <Group gap="xs" wrap="wrap">
              {temRascunho && (
                <SegmentedControl
                  size="xs"
                  value={vendo}
                  onChange={(v) => setVendo(v as 'rascunho' | 'publicada')}
                  data={[
                    { value: 'rascunho', label: 'Rascunho' },
                    { value: 'publicada', label: 'No ar' },
                  ]}
                />
              )}
              <SegmentedControl
                size="xs"
                value={horario}
                onChange={(v) => setHorario(v as 'em' | 'fora')}
                data={[
                  { value: 'em', label: 'Em horário' },
                  { value: 'fora', label: 'Fora do horário' },
                ]}
              />
              {/* Só com o menu por tipo LIGADO: desligado, todo mundo vê o
                  mesmo menu e o alternador prometeria uma diferença que não
                  existe. */}
              {menuPorTipoAtivo && (
                <SegmentedControl
                  size="xs"
                  value={perfil}
                  onChange={(v) => setPerfil(v as PerfilDaPrevia)}
                  aria-label="Perfil da prévia"
                  data={[
                    { value: 'aluno', label: 'Aluno' },
                    { value: 'instrutor', label: 'Instrutor' },
                    { value: 'interessado', label: 'Interessado' },
                  ]}
                />
              )}
              <Button size="xs" variant="subtle" onClick={abrirHistorico}>
                Histórico
              </Button>
              {capacidades.administrar && temRascunho && (
                <>
                  <Button size="xs" variant="filled" onClick={abrirPublicar}>
                    Publicar
                  </Button>
                  <Button size="xs" variant="subtle" color="red" onClick={abrirDescartar}>
                    Descartar rascunho
                  </Button>
                </>
              )}
            </Group>
          </Group>

          {temRascunho ? (
            <Alert color="orange" variant="light" title="Há um rascunho em aberto">
              O aluno continua recebendo a versão no ar. {mudancas.length} mudança(s) esperando
              publicação
              {capacidades.administrar ? '.' : ' — só um admin publica.'}
            </Alert>
          ) : (
            <Text size="sm" c="dimmed">
              Sem rascunho. A primeira edição abre um, e ele fica compartilhado até alguém
              publicar ou descartar.
            </Text>
          )}

          <Group gap="sm" wrap="wrap">
            <Text size="xs" c="dimmed">
              O <strong>{arvore.reservado.numero}</strong> ({arvore.reservado.label}) é injetado
              pelo bot em todo nó e <strong>não entra na numeração</strong>; o{' '}
              <strong>0</strong> (voltar) também. Sobram {arvore.limites.maxOpcoes} números por nó.
            </Text>
            {!capacidades.editar && (
              <Text size="xs" c="dimmed">
                Seu papel não edita a árvore.
              </Text>
            )}
          </Group>
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------
          As duas colunas. No celular, uma por vez.
      ------------------------------------------------------------------ */}
      {/*
        `align="flex-start"` e NADA de `stretch`: a coluna esquerda precisa ser
        mais curta que o container para poder grudar. Com `stretch` ela teria a
        altura do painel direito — duas telas — e `sticky` não teria onde
        atuar.
      */}
      <Group align="flex-start" gap="md" wrap="nowrap">
        <Box
          w={{ base: '100%', sm: 360 }}
          miw={{ sm: 320 }}
          display={{ base: editandoNoCelular ? 'none' : 'block', sm: 'block' }}
          /*
            A árvore é NAVEGAÇÃO: sumir ao rolar a edição derrota o propósito.
            Então ela gruda no topo e acompanha a janela. O desconto de 72px é
            a barra do shell (56) mais o respiro da página.
          */
          style={{
            flexShrink: 0,
            position: 'sticky',
            top: 72,
            alignSelf: 'flex-start',
          }}
        >
          <Card withBorder padding="xs">
            <Stack gap={4}>
              <Group justify="space-between" wrap="nowrap">
                <Text size="xs" tt="uppercase" fw={600} c="dimmed" style={{ letterSpacing: '0.06em' }}>
                  A árvore
                </Text>
                <Text size="xs" c="dimmed">
                  {arvore.nos.length} nós
                </Text>
              </Group>

              {/*
                `Autosize` + `mah` em viewport: cresce com o conteúdo e só
                ganha barra própria quando a árvore passa da tela. Antes o
                limite era 360px de altura fixa, e a árvore parava em
                "produtos" num espaço onde caberia inteira.
              */}
              <ScrollArea.Autosize
                mah={ajuda ? 'calc(100vh - 520px)' : 'calc(100vh - 240px)'}
                type="auto"
              >
                <Stack gap={2} pr="xs">
                  {estrutura.raiz ? (
                    <RamoDaArvore
                      menuPorTipoAtivo={menuPorTipoAtivo}
                      noId={estrutura.raiz.noId}
                      nos={arvore.nos}
                      filhos={estrutura.filhos}
                      nivel={0}
                      selecionado={selecionado}
                      expandidos={expandidos}
                      alterados={nosAlterados}
                      problemas={problemasPorNo}
                      aoSelecionar={(id) => {
                        setSelecionado(id);
                        setEditandoNoCelular(true);
                      }}
                      aoAlternar={alternar}
                    />
                  ) : (
                    <Text size="sm" c="dimmed">
                      Árvore sem raiz.
                    </Text>
                  )}

                  {/* Nós que nenhuma opção alcança — o bot os empilha por ação. */}
                  {estrutura.ordem
                    .filter(({ no, nivel }) => nivel === 0 && !no.ehRaiz)
                    .map(({ no }) => (
                      <RamoDaArvore
                        menuPorTipoAtivo={menuPorTipoAtivo}
                        key={no.noId}
                        noId={no.noId}
                        nos={arvore.nos}
                        filhos={estrutura.filhos}
                        nivel={0}
                        selecionado={selecionado}
                        expandidos={expandidos}
                        alterados={nosAlterados}
                        problemas={problemasPorNo}
                        aoSelecionar={(id) => {
                          setSelecionado(id);
                          setEditandoNoCelular(true);
                        }}
                        aoAlternar={alternar}
                      />
                    ))}
                </Stack>
              </ScrollArea.Autosize>

              <Text size="xs" c="dimmed">
                ▸ abre outro nó · → responde uma FAQ · 💬 pergunta livre · ⇥ passa para a
                secretaria
              </Text>
            </Stack>
          </Card>

          {/*
            Como o editor funciona, para quem abre pela primeira vez e para a
            secretaria, que não acompanhou a construção.

            FECHADA por padrão: quem já sabe não perde a área da árvore. O que
            já está dito em cima — o rascunho e os números reservados — é
            REFERENCIADO, não repetido: dois textos com a mesma regra divergem
            na primeira vez que um deles muda.
          */}
          <Box mt="xs">
            <BlocoDeAjuda
              aoAlternar={setAjuda}
              secoes={[
                {
                  id: 'como',
                  titulo: 'Como o editor funciona',
                  corpo: (
                    <>
                      <Text size="sm">
                        <strong>Nada vai para o ar quando você salva.</strong> Toda edição entra
                        num rascunho, e o bot segue respondendo a versão no ar até alguém publicar.
                        O aviso no alto da tela diz se há rascunho aberto e quantas mudanças
                        esperam. O alternador <em>Rascunho / No ar</em>, ao lado, troca qual das
                        duas você está vendo — a tela abre no rascunho.
                      </Text>
                      <Text size="sm">
                        <strong>Editor e admin editam. Só admin publica</strong> ou descarta o
                        rascunho. O rascunho é um só, compartilhado: se duas pessoas editam no
                        mesmo dia, as mudanças das duas vão juntas na mesma publicação.
                      </Text>
                      <Text size="sm">
                        <strong>Cada nó cabe oito opções</strong> — o motivo está no alto da tela.
                        Na árvore, cada nó mostra quantas tem e quantas sobram; cheio, mostra{' '}
                        <strong>8/cheio</strong> em laranja. Para acrescentar num nó cheio, a saída
                        é <strong>aninhar</strong>: mover uma opção para dentro de outro nó libera
                        a vaga. O nó que recebe também precisa ter vaga — nó cheio não aparece como
                        destino.
                      </Text>
                      <Text size="sm">
                        <strong>Opção se remove. Nó se desativa.</strong> Desativar não apaga: tira
                        o caminho do menu na próxima publicação, e o nó continua existindo porque o
                        histórico de atendimentos aponta para ele. Antes de confirmar, a tela
                        mostra{' '}
                        <strong>
                          quantos atendimentos passaram por aquele nó nos últimos 30 dias
                        </strong>{' '}
                        — desativar um nó por onde passaram quarenta pessoas é decisão diferente de
                        desativar um que ninguém usou.
                      </Text>
                      <Text size="sm">
                        <strong>O identificador do nó não muda depois de criado.</strong> O
                        histórico aponta para os nós por ele. Dá para trocar o texto que o aluno lê
                        a qualquer momento; o identificador, não.
                      </Text>
                      <Text size="sm">
                        <strong>Nó sem nenhuma opção apontando para ele</strong> aparece solto no
                        fim da lista, com o selo <em>alcançado por ação</em>: o bot chega nele por
                        uma ação — transbordo, pergunta livre — e não por número digitado. Não é
                        erro.
                      </Text>
                      <Text size="sm" c="dimmed">
                        Em vermelho, <em>“Este nó impede a publicação”</em> é a mesma validação que
                        o Publicar roda. Enquanto houver um desses, a publicação não passa.
                      </Text>
                    </>
                  ),
                },
              ]}
            />
          </Box>
        </Box>

        <Box
          style={{ flex: 1, minWidth: 0 }}
          display={{ base: editandoNoCelular ? 'block' : 'none', sm: 'block' }}
        >
          {noAberto ? (
            <PainelDoNo
              key={noAberto.id}
              no={noAberto}
              arvore={arvore}
              editavel={editavel}
              salvando={salvando}
              horario={horario}
              perfil={perfil}
              temMudanca={nosAlterados.has(noAberto.noId)}
              problemas={problemasPorNo.get(noAberto.noId) ?? []}
              editandoEstrutura={editavel}
              aoVoltar={() => setEditandoNoCelular(false)}
              aoNovaOpcao={() => setDialogo({ tipo: 'nova_opcao', noId: noAberto.noId })}
              aoMover={() => setDialogo({ tipo: 'mover', noId: noAberto.noId })}
              aoDesativar={() => setDialogo({ tipo: 'desativar', noId: noAberto.noId })}
              aoReativar={() =>
                salvar(
                  () => api.post(`/admin/menu/nos/${noAberto.noId}/reativar`, {}),
                  'Nó reativado no rascunho.',
                )
              }
              abrindoFaq={abrindoFaq}
              aoAbrirFaq={(faqId) => void abrirFaq(faqId)}
              menuPorTipoAtivo={menuPorTipoAtivo}
              aoTrocarVisibilidade={(o) =>
                setDialogo({
                  tipo: 'visibilidade',
                  opcaoId: o.id,
                  label: o.label,
                  regraAtual: o.visivelRegra,
                })
              }
              aoTrocarDestino={(o) =>
                setDialogo({
                  tipo: 'trocar_destino',
                  opcaoId: o.id,
                  label: o.label,
                  destinoAtual: o.destinoLegivel,
                })
              }
              aoRemoverOpcao={(o) =>
                salvar(
                  () => api.delete(`/admin/menu/opcoes/${o.id}`),
                  'Opção removida do rascunho.',
                )
              }
              aoSalvarTexto={(campo, valor) =>
                salvar(
                  () => api.patch('/admin/menu/texto', { noId: noAberto.noId, campo, valor }),
                  'Texto salvo no rascunho.',
                )
              }
              aoSalvarRotulo={(opcaoId, label) =>
                salvar(
                  () => api.patch('/admin/menu/rotulo', { opcaoId, label }),
                  'Rótulo salvo no rascunho.',
                )
              }
              aoReordenar={(ids) =>
                salvar(
                  () => api.patch('/admin/menu/ordem', { noId: noAberto.noId, opcoes: ids }),
                  'Ordem salva no rascunho.',
                )
              }
            />
          ) : (
            <Vazio titulo="Escolha um nó à esquerda.">
              A edição do nó aparece aqui: textos, prévia de como o aluno vê, e as opções com a
              numeração que ele digita.
            </Vazio>
          )}
        </Box>
      </Group>

      {/* ------------------------------------------------------------------
          Histórico: modal pelo cabeçalho. Antes ficava no fim de uma rolagem
          de nove nós, onde ninguém chegava.
      ------------------------------------------------------------------ */}
      {/* A FAQ aberta por uma opção: o mesmo editor da aba FAQs. */}
      {faqAberta && contextoDeFaq && (
        <EditorFaq
          faq={faqAberta}
          capacidades={capacidades}
          categorias={contextoDeFaq.categorias}
          categoriaPendenteConteudo={contextoDeFaq.categoriaPendenteConteudo}
          aoFechar={() => setFaqAberta(null)}
          aoSalvar={(atualizada) => {
            setFaqAberta(null);
            notifications.show({ color: 'green', message: 'FAQ salva.' });
            // Recarrega a árvore: o selo da opção mostra a pergunta da FAQ, e
            // ela pode ter mudado agora mesmo.
            void carregar();
            void atualizada;
          }}
        />
      )}

      {/* ------------------------------------------------------------------
          Diálogos de estrutura. Cada um recarrega a árvore ao concluir: a
          validação e o resumo mudam junto.
      ------------------------------------------------------------------ */}
      {dialogo?.tipo === 'nova_opcao' && noAberto && (
        <ModalNovaOpcao
          no={noAberto}
          capacidades={capacidades}
          aoFechar={() => setDialogo(null)}
          aoCriado={() => {
            setDialogo(null);
            notifications.show({ color: 'green', message: 'Opção criada no rascunho.' });
            void carregar();
          }}
        />
      )}

      {dialogo?.tipo === 'trocar_destino' && (
        <ModalTrocarDestino
          opcaoId={dialogo.opcaoId}
          label={dialogo.label}
          destinoAtual={dialogo.destinoAtual}
          capacidades={capacidades}
          aoFechar={() => setDialogo(null)}
          aoTrocado={(semEntrada) => {
            setDialogo(null);
            notifications.show({
              color: semEntrada.length > 0 ? 'orange' : 'green',
              title: 'Destino trocado no rascunho',
              message:
                semEntrada.length > 0
                  ? `Sem caminho de entrada agora: ${semEntrada.join(', ')}. Resolva antes de publicar.`
                  : 'Pronto.',
            });
            void carregar();
          }}
        />
      )}

      {dialogo?.tipo === 'mover' && noAberto && (
        <ModalMoverNo
          no={noAberto}
          nos={arvore.nos}
          aoFechar={() => setDialogo(null)}
          aoMovido={() => {
            setDialogo(null);
            notifications.show({ color: 'green', message: 'Caminho movido no rascunho.' });
            void carregar();
          }}
        />
      )}

      {dialogo?.tipo === 'visibilidade' && (
        <ModalVisibilidade
          opcaoId={dialogo.opcaoId}
          label={dialogo.label}
          regraAtual={dialogo.regraAtual}
          menuPorTipoAtivo={menuPorTipoAtivo}
          aoFechar={() => setDialogo(null)}
          aoDefinido={() => {
            setDialogo(null);
            notifications.show({ color: 'green', message: 'Visibilidade definida no rascunho.' });
            void carregar();
          }}
        />
      )}

      {dialogo?.tipo === 'desativar' && noAberto && (
        <ModalDesativarNo
          no={noAberto}
          aoFechar={() => setDialogo(null)}
          aoDesativado={() => {
            setDialogo(null);
            notifications.show({ color: 'green', message: 'Nó desativado no rascunho.' });
            void carregar();
          }}
        />
      )}

      <Modal
        opened={vendoHistorico}
        onClose={fecharHistorico}
        title="Histórico de versões"
        size="xl"
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Restaurar cria um rascunho a partir da versão escolhida — nunca publica direto.
          </Text>
          <Historico
            itens={historico ?? []}
            podeRestaurar={capacidades.administrar}
            temRascunho={temRascunho}
            salvando={salvando}
            aoRestaurar={(id) => {
              fecharHistorico();
              void salvar(
                () => api.post(`/admin/menu/versoes/${id}/restaurar`, {}),
                'Rascunho criado a partir da versão escolhida.',
              );
            }}
          />
        </Stack>
      </Modal>

      {/* ------------------------------------------------------------------
          Publicar: o resumo vem ANTES da confirmação.
      ------------------------------------------------------------------ */}
      <Modal opened={publicando} onClose={fecharPublicar} title="Publicar a árvore" size="lg">
        <Stack gap="md">
          {mudancas.length === 0 ? (
            <Alert color="yellow" variant="light">
              O rascunho não tem diferença nenhuma em relação ao que está no ar. Publicar só
              trocaria o número da versão.
            </Alert>
          ) : (
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                {mudancas.length} mudança(s) vão ao ar:
              </Text>
              <ResumoDeMudancas mudancas={mudancas} />
            </Stack>
          )}

          <TextInput
            label="Nota desta versão"
            description="aparece no histórico; ajuda a entender depois por que mudou"
            value={nota}
            onChange={(e) => setNota(e.currentTarget.value)}
          />

          <Text size="xs" c="dimmed">
            Ao publicar, esta versão vai ao ar e a atual é arquivada. O bot passa a usar a nova em
            segundos, sem reinício.
          </Text>

          <Group justify="flex-end">
            <Button onClick={fecharPublicar}>Cancelar</Button>
            <Button variant="filled" loading={salvando} onClick={() => void publicar()}>
              Publicar
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={descartando}
        onClose={fecharDescartar}
        title="Descartar o rascunho?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            As {mudancas.length} mudança(s) escritas por {arvore.versao.alteradaPorNome ?? 'alguém'}{' '}
            são <strong>apagadas</strong>. A versão no ar não muda, e o que o rascunho continha
            fica registrado na auditoria.
          </Text>
          <Group justify="flex-end">
            <Button onClick={fecharDescartar}>Cancelar</Button>
            <Button
              color="red"
              variant="filled"
              loading={salvando}
              onClick={() => {
                fecharDescartar();
                void salvar(
                  () => api.post('/admin/menu/descartar', {}),
                  'Rascunho descartado.',
                );
              }}
            >
              Descartar mesmo assim
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Coluna esquerda
// ---------------------------------------------------------------------------

/**
 * Um nó na árvore lateral, com suas opções e seus filhos.
 *
 * Recursivo e recolhível. O que cabe aqui é o ESSENCIAL: nome do nó, número
 * que o aluno digita, símbolo do destino, contagem de opções e vagas, e a
 * marca de alteração não publicada. Texto completo e edição ficam na direita.
 */
function RamoDaArvore({
  noId,
  nos,
  filhos,
  nivel,
  selecionado,
  expandidos,
  alterados,
  problemas,
  menuPorTipoAtivo,
  aoSelecionar,
  aoAlternar,
}: {
  menuPorTipoAtivo: boolean;
  noId: string;
  nos: readonly NoDoMenu[];
  filhos: Map<string, string[]>;
  nivel: number;
  selecionado: string | null;
  expandidos: ReadonlySet<string>;
  alterados: ReadonlySet<string>;
  /** Problemas de validação por nó — marcados na árvore, não numa lista. */
  problemas: ReadonlyMap<string, ProblemaDoMenu[]>;
  aoSelecionar: (noId: string) => void;
  aoAlternar: (noId: string) => void;
}): JSX.Element | null {
  const no = nos.find((n) => n.noId === noId);
  if (!no) return null;

  const aberto = expandidos.has(noId);
  const meusFilhos = filhos.get(noId) ?? [];
  const ativo = selecionado === noId;
  const alterado = alterados.has(noId);
  const osProblemas = problemas.get(noId) ?? [];
  // Uma vez por nó, não por opção: a pergunta é sobre o conjunto.
  const diferencia = noDiferencia(no.opcoes, menuPorTipoAtivo);

  return (
    <Box>
      <Paper
        withBorder={ativo}
        bg={ativo ? 'var(--mantine-primary-color-light)' : undefined}
        p={4}
        radius="sm"
        ml={nivel * 12}
      >
        <Group gap={4} wrap="nowrap">
          {/* Recolher/expandir é separado de selecionar: abrir um ramo não
              deveria trocar o que está sendo editado à direita. */}
          {no.opcoes.length > 0 ? (
            <ActionIcon
              size="xs"
              variant="subtle"
              aria-label={aberto ? `Recolher ${noId}` : `Expandir ${noId}`}
              onClick={() => aoAlternar(noId)}
            >
              {aberto ? '▾' : '▸'}
            </ActionIcon>
          ) : (
            <Box w={18} />
          )}

          <UnstyledButton
            onClick={() => aoSelecionar(noId)}
            style={{ flex: 1, minWidth: 0 }}
            aria-current={ativo ? 'true' : undefined}
          >
            <Group gap={6} wrap="nowrap" justify="space-between">
              <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                <Text
                  size="sm"
                  fw={ativo ? 700 : 500}
                  truncate="end"
                  /* Nó desativado continua na lista, apagado: ele existe no
                     registro e pode ser reativado. */
                  td={no.ativo ? undefined : 'line-through'}
                  c={no.ativo ? undefined : 'dimmed'}
                >
                  {noId}
                </Text>
                {/* Problema de validação: símbolo com a frase no toque, e a
                    lista inteira no painel do nó. */}
                {osProblemas.length > 0 && (
                  <Tooltip label={osProblemas.map((p) => p.mensagem).join(' · ')} multiline w={260}>
                    <Badge size="xs" color="red" variant="filled">
                      ⚠
                    </Badge>
                  </Tooltip>
                )}
                {!no.ativo && (
                  <Badge size="xs" color="gray" variant="light">
                    desativado
                  </Badge>
                )}
                {/* Marca de rascunho não publicado NESTE nó: texto no toque,
                    não só o ponto. */}
                {alterado && (
                  <Tooltip label="tem alteração não publicada">
                    <Badge size="xs" color="orange" variant="filled" circle>
                      •
                    </Badge>
                  </Tooltip>
                )}
                {no.paisDiretos.length > 1 && (
                  <Tooltip label={`alcançado por mais de um caminho: ${no.paisDiretos.join(', ')}`}>
                    <Badge size="xs" color="gray" variant="light">
                      ⇇
                    </Badge>
                  </Tooltip>
                )}
              </Group>
              <Text size="xs" c={no.vagasRestantes === 0 ? 'orange' : 'dimmed'} style={{ whiteSpace: 'nowrap' }}>
                {no.opcoes.length}/{no.vagasRestantes === 0 ? 'cheio' : `${no.vagasRestantes} livre`}
              </Text>
            </Group>
          </UnstyledButton>
        </Group>
      </Paper>

      {aberto && (
        <Stack gap={2} mt={2}>
          {no.opcoes.map((o) => (
            <Group
              key={o.id}
              gap={6}
              wrap="nowrap"
              align="flex-start"
              ml={(nivel + 1) * 12 + 18}
            >
              <Text size="xs" c="dimmed" ff="monospace" w={14}>
                {o.numero ?? '?'}
              </Text>
              <Tooltip label={o.destinoLegivel} multiline w={240}>
                <Text size="xs" c={COR_DESTINO[o.tipoDeDestino]} w={14}>
                  {SIMBOLO_DESTINO[o.tipoDeDestino]}
                </Text>
              </Tooltip>
              {/* "todos" só quando ALGUMA opção deste nó tem regra valendo:
                  é ali que a ausência vira informação. Nó sem diferenciação
                  nenhuma fica limpo. */}
              {!o.visivelRegra && diferencia && (
                <Tooltip label="sem regra: todo mundo vê esta opção">
                  <Badge size="xs" px={4} color="gray" variant="outline" opacity={0.6}>
                    {MARCA_TODOS}
                  </Badge>
                </Tooltip>
              )}
              {/* Quem vê, em forma compacta. É nesta coluna que se vê o
                  CONJUNTO, e o conjunto é o que importa na hora de decidir
                  quem vê o quê — o painel do nó mostra uma opção por vez. */}
              {o.visivelRegra && (
                <Tooltip
                  label={
                    regraInerte(o.visivelRegra, menuPorTipoAtivo)
                      ? `${DESCRICAO_REGRA[o.visivelRegra] ?? o.visivelRegra} — mas o menu por tipo está DESLIGADO, então ela não esconde nada hoje`
                      : (DESCRICAO_REGRA[o.visivelRegra] ?? o.visivelRegra)
                  }
                  multiline
                  w={280}
                >
                  <Badge
                    size="xs"
                    px={4}
                    color={regraInerte(o.visivelRegra, menuPorTipoAtivo) ? 'gray' : 'yellow'}
                    variant={regraInerte(o.visivelRegra, menuPorTipoAtivo) ? 'outline' : 'light'}
                  >
                    {MARCA_REGRA[o.visivelRegra] ?? '•'}
                  </Badge>
                </Tooltip>
              )}
              {/*
                DUAS linhas antes de cortar, não uma.
                "Cursos Práticos (PPA, PCA, IFRA, MLTE, INVA)" cortava em
                "MLTE," com uma linha — e duas opções de nome parecido ficavam
                indistinguíveis sem passar o mouse. A coluna também ficou mais
                larga (360px); o `title` continua com a frase inteira para o
                caso raro de três linhas.
              */}
              {o.proximoNo ? (
                <UnstyledButton
                  onClick={() => aoSelecionar(o.proximoNo as string)}
                  style={{ flex: 1, minWidth: 0 }}
                  title={o.label}
                >
                  <Text size="xs" lineClamp={2} style={{ textAlign: 'left' }}>
                    {o.label}
                  </Text>
                </UnstyledButton>
              ) : (
                <Text
                  size="xs"
                  c="dimmed"
                  lineClamp={2}
                  title={o.label}
                  style={{ flex: 1, minWidth: 0 }}
                >
                  {o.label}
                </Text>
              )}
            </Group>
          ))}

          {meusFilhos.map((filho) => (
            <RamoDaArvore
              menuPorTipoAtivo={menuPorTipoAtivo}
              key={filho}
              noId={filho}
              nos={nos}
              filhos={filhos}
              nivel={nivel + 1}
              selecionado={selecionado}
              expandidos={expandidos}
              alterados={alterados}
              problemas={problemas}
              aoSelecionar={aoSelecionar}
              aoAlternar={aoAlternar}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Coluna direita
// ---------------------------------------------------------------------------

/**
 * O nó selecionado, com tudo o que a tela de uma coluna já mostrava.
 *
 * Nada saiu: campos de texto (com as variantes de nome e de horário), contagem
 * de caracteres com aviso, prévia pelo mesmo renderizador da FAQ, e as opções
 * com rótulo, destino em texto e setas de ordem.
 */
function PainelDoNo({
  no,
  arvore,
  editavel,
  salvando,
  horario,
  perfil,
  temMudanca,
  problemas,
  editandoEstrutura,
  aoVoltar,
  aoSalvarTexto,
  aoSalvarRotulo,
  aoReordenar,
  aoNovaOpcao,
  aoMover,
  aoDesativar,
  aoReativar,
  aoTrocarDestino,
  aoTrocarVisibilidade,
  menuPorTipoAtivo,
  aoRemoverOpcao,
  aoAbrirFaq,
  abrindoFaq,
}: {
  no: NoDoMenu;
  arvore: ArvoreDoMenu;
  perfil: PerfilDaPrevia;
  editavel: boolean;
  salvando: boolean;
  horario: 'em' | 'fora';
  temMudanca: boolean;
  problemas: readonly ProblemaDoMenu[];
  /** Mesmo critério de `editavel`: ações de estrutura só no rascunho. */
  editandoEstrutura: boolean;
  aoVoltar: () => void;
  aoSalvarTexto: (campo: CampoDeTextoMenu, valor: string) => void;
  aoSalvarRotulo: (opcaoId: string, label: string) => void;
  aoReordenar: (ids: string[]) => void;
  aoNovaOpcao: () => void;
  aoMover: () => void;
  aoDesativar: () => void;
  aoReativar: () => void;
  aoTrocarDestino: (opcao: OpcaoDoMenu) => void;
  aoTrocarVisibilidade: (opcao: OpcaoDoMenu) => void;
  menuPorTipoAtivo: boolean;
  aoRemoverOpcao: (opcao: OpcaoDoMenu) => void;
  /** Abre a FAQ que a opção responde, no editor de FAQ. */
  aoAbrirFaq: (faqId: string) => void;
  abrindoFaq: boolean;
}): JSX.Element {
  /** Aberta por padrão: é a razão de a prévia existir. */
  const [comPrevia, setComPrevia] = useState(true);

  const campos = (
    [
      'texto',
      'textoSemAssunto',
      'saudacao',
      'saudacaoSemNome',
      'textoForaHorario',
      'textoProximaAbertura',
    ] as CampoDeTextoMenu[]
  ).filter((c) => no[c] !== null);

  /**
   * Prévia do que o aluno recebe.
   *
   * Monta na mesma ordem do bot: saudação, texto, opções numeradas e — fora do
   * horário — o rodapé. Os marcadores entram com valores de exemplo, porque
   * `{nome}` cru não mostra o que a pessoa vai ler.
   */
  const previa = [
    no.saudacao ? comExemplos(no.saudacao) : null,
    comExemplos(no.texto),
    no.opcoes.length > 0
      ? no.opcoes
          .filter((o) => opcaoVisivel(o.visivelRegra, { horario, perfil, menuPorTipoAtivo }))
          .map((o, i) => `*${i + 1}* - ${o.label}`)
          .join('\n')
      : null,
    `*${arvore.reservado.numero}* - ${arvore.reservado.label}`,
    !no.ehRaiz ? '*0* - Voltar' : null,
    horario === 'fora' && no.textoForaHorario
      ? [
          comExemplos(no.textoForaHorario),
          no.textoProximaAbertura ? comExemplos(no.textoProximaAbertura) : null,
        ]
          .filter(Boolean)
          .join('\n\n')
      : null,
  ]
    .filter((p): p is string => typeof p === 'string' && p.length > 0)
    .join('\n\n');

  return (
    <Card withBorder padding="md">
      <Stack gap="sm">
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Group gap="xs" wrap="wrap">
            {/* No celular a lista sai da tela; este é o caminho de volta. */}
            <Button size="compact-xs" variant="subtle" hiddenFrom="sm" onClick={aoVoltar}>
              ← Árvore
            </Button>
            <Code>{no.noId}</Code>
            {no.ehRaiz && <Badge color="azul">raiz</Badge>}
            {temMudanca && (
              <Badge color="orange" variant="light">
                alteração não publicada
              </Badge>
            )}
            {no.paisDiretos.length > 0 && (
              <Text size="xs" c="dimmed">
                aparece em: {no.paisDiretos.join(', ')}
              </Text>
            )}
            {no.paisDiretos.length === 0 && !no.ehRaiz && (
              <Tooltip label="nenhuma opção aponta para cá; o bot empilha este nó por ação">
                <Badge color="gray" size="xs">
                  alcançado por ação
                </Badge>
              </Tooltip>
            )}
            {no.categoriaDaBusca && (
              <Badge color="violet" size="xs">
                busca filtrada por {no.categoriaDaBusca}
              </Badge>
            )}
          </Group>
          <Group gap="xs" wrap="nowrap">
            <Text size="xs" c={no.vagasRestantes === 0 ? 'orange' : 'dimmed'}>
              {no.opcoes.length} de {arvore.limites.maxOpcoes} opções ·{' '}
              {no.vagasRestantes === 0 ? 'no teto' : `${no.vagasRestantes} vaga(s)`}
            </Text>
            <Button size="compact-xs" variant="subtle" onClick={() => setComPrevia((v) => !v)}>
              {comPrevia ? 'Ocultar prévia' : 'Mostrar prévia'}
            </Button>
          </Group>
        </Group>

        {/* Problemas do nó, com o que fazer. É a mesma validação que bloqueia
            a publicação — aqui ela é aviso. */}
        {problemas.length > 0 && (
          <Alert color="red" variant="light" title="Este nó impede a publicação">
            <Stack gap={4}>
              {problemas.map((p) => (
                <Text size="sm" key={`${p.tipo}-${p.mensagem}`}>
                  • {p.mensagem} — <strong>{p.oQueFazer}</strong>
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        {!no.ativo && (
          <Alert color="gray" variant="light" title="Nó desativado">
            Ele sai da árvore na próxima publicação. O registro continua: o histórico de
            atendimentos aponta para <Code>{no.noId}</Code>.
          </Alert>
        )}

        {/* Ações de estrutura do nó. */}
        {editandoEstrutura && (
          <Group gap="xs" wrap="wrap">
            <Button size="xs" variant="light" onClick={aoNovaOpcao}>
              Nova opção
            </Button>
            {!no.ehRaiz && (
              <Button size="xs" variant="subtle" onClick={aoMover}>
                Mover um caminho até aqui
              </Button>
            )}
            {!no.ehRaiz &&
              (no.ativo ? (
                <Button size="xs" variant="subtle" color="red" onClick={aoDesativar}>
                  Desativar nó
                </Button>
              ) : (
                <Button size="xs" variant="subtle" onClick={aoReativar}>
                  Reativar nó
                </Button>
              ))}
          </Group>
        )}

        {/* Marcadores: do NÓ, uma vez, aqui. Antes repetiam sob cada campo. */}
        {no.marcadores.length > 0 && (
          <Text size="xs" c="dimmed">
            Marcadores deste nó: {no.marcadores.map((m) => `{${m}}`).join(', ')} — são trocados
            pelo dado real na hora do envio.
          </Text>
        )}

        {/*
          Campos à esquerda, prévia à direita — num `Group` que QUEBRA.
          Em tela larga a prévia fica ao lado e GRUDADA, acompanhando a edição
          sem rolagem; quando o painel afina, a prévia desce para baixo dos
          campos sozinha, pelo `wrap`, que é o arranjo de antes. Sem media
          query e sem markup duplicado: a largura decide.
        */}
        <Group align="flex-start" gap="md" wrap="wrap">
          {/* Esquerda: os textos e as opções, na mesma coluna de edição. */}
          <Stack gap="sm" style={{ flex: '1 1 420px', minWidth: 0 }}>
            {campos.map((campo) => (
              <CampoDeTexto
                key={campo}
                rotulo={ROTULO_CAMPO[campo]}
                valor={no[campo] ?? ''}
                editavel={editavel}
                salvando={salvando}
                aoSalvar={(valor) => aoSalvarTexto(campo, valor)}
              />
            ))}

            {/* Opções, em ordem, com destino e numeração. */}
            {no.opcoes.length > 0 && (
              <Stack gap={6}>
                <Group gap="xs" align="baseline" wrap="wrap">
                  <Text size="sm" fw={600}>
                    Opções
                  </Text>
                  {/*
                    O caso que os selos NÃO cobrem.
                    Com o selo "todos" condicionado a haver contraste, um nó em
                    que ninguém marcou nada fica sem selo nenhum — e aí a
                    ausência volta a ser ambígua: "não tem regra" ou "a tela não
                    mostra"? Uma linha por NÓ responde isso, em vez de um selo
                    por opção. Só com o menu por tipo ligado: desligado, não há
                    o que diferenciar e a frase seria obviedade.
                  */}
                  {menuPorTipoAtivo && !noDiferencia(no.opcoes, menuPorTipoAtivo) && (
                    <Text size="xs" c="dimmed">
                      · nenhuma tem regra de visibilidade: todo mundo vê as mesmas{' '}
                      {no.opcoes.length}
                    </Text>
                  )}
                </Group>
                {no.opcoes.map((o, i) => (
                  <LinhaDeOpcao
                    key={o.id}
                    opcao={o}
                    limite={arvore.limites.limiteRotulo}
                    editavel={editavel}
                    salvando={salvando}
                    primeira={i === 0}
                    ultima={i === no.opcoes.length - 1}
                    aoTrocarDestino={editandoEstrutura ? () => aoTrocarDestino(o) : undefined}
                    noDiferencia={noDiferencia(no.opcoes, menuPorTipoAtivo)}
                    aoTrocarVisibilidade={
                      editandoEstrutura ? () => aoTrocarVisibilidade(o) : undefined
                    }
                    menuPorTipoAtivo={menuPorTipoAtivo}
                    aoRemover={editandoEstrutura ? () => aoRemoverOpcao(o) : undefined}
                    aoAbrirFaq={aoAbrirFaq}
                    abrindoFaq={abrindoFaq}
                    aoSalvar={(label) => aoSalvarRotulo(o.id, label)}
                    aoMover={(direcao) => {
                      const ids = no.opcoes.map((x) => x.id);
                      const destino = direcao === 'cima' ? i - 1 : i + 1;
                      if (destino < 0 || destino >= ids.length) return;
                      [ids[i], ids[destino]] = [ids[destino]!, ids[i]!];
                      aoReordenar(ids);
                    }}
                  />
                ))}
                <Text size="xs" c="dimmed">
                  O <strong>{arvore.reservado.numero}</strong> ({arvore.reservado.label}) entra
                  depois destas, sempre, e não ocupa número da lista.
                </Text>
              </Stack>
            )}
          </Stack>

          {/* Prévia: o mesmo renderizador da prévia de FAQ. */}
          {comPrevia && (
            <Box
              style={{
                flex: '0 1 440px',
                minWidth: 280,
                position: 'sticky',
                top: 72,
                alignSelf: 'flex-start',
              }}
            >
              <Text size="xs" c="dimmed" mb={4}>
                {/* Com o menu por tipo ligado, "o aluno" deixa de ser uma
                    pessoa só: a numeração muda por perfil, e o cabeçalho tem
                    de dizer de quem é a visão que está na tela. */}
                {menuPorTipoAtivo ? 'Como ' : 'Como o aluno vê '}
                {menuPorTipoAtivo && (
                  <Text span fw={600}>
                    {perfil === 'aluno'
                      ? 'o aluno'
                      : perfil === 'instrutor'
                        ? 'o instrutor'
                        : 'o interessado'}
                  </Text>
                )}
                {menuPorTipoAtivo ? ' vê ' : ''}
                {horario === 'fora' ? '(fora do horário)' : '(em horário)'} — com valores de
                exemplo nos marcadores
              </Text>
              <Paper className="previa-bolha" p="sm" radius="md">
                <CorpoWhatsapp texto={previa} />
              </Paper>
            </Box>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

function ResumoDeMudancas({
  mudancas,
}: {
  mudancas: readonly import('../lib/api.js').MudancaDoMenu[];
}): JSX.Element {
  /**
   * Estrutura primeiro.
   *
   * Renomear um rótulo erra uma palavra; trocar um destino manda o aluno para
   * outro lugar. O que é mais arriscado é o que a pessoa tem de ler antes de
   * clicar em publicar.
   */
  const ordenadas = [...mudancas].sort((a, b) => {
    const pesoA = ehMudancaEstrutural(a) ? 0 : 1;
    const pesoB = ehMudancaEstrutural(b) ? 0 : 1;
    return pesoA - pesoB;
  });

  return (
    <Stack gap="xs">
      {ordenadas.map((m, i) => (
        <Paper
          withBorder
          p="xs"
          radius="sm"
          key={`${m.tipo}-${m.noId}-${i}`}
          /* Estrutural fica destacada: borda e selo. */
          bg={ehMudancaEstrutural(m) ? 'var(--mantine-color-orange-light)' : undefined}
        >
          {ehMudancaEstrutural(m) && (
            <Badge size="xs" color="orange" mb={4}>
              estrutura
            </Badge>
          )}
          {m.tipo === 'rotulo' && (
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                rótulo em <Code>{m.noId}</Code> · {m.destinoLegivel}
              </Text>
              <Text size="sm" td="line-through" c="dimmed">
                {m.antes}
              </Text>
              <Text size="sm">{m.depois}</Text>
            </Stack>
          )}
          {m.tipo === 'texto' && (
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                {ROTULO_CAMPO[m.campo as CampoDeTextoMenu] ?? m.campo} em <Code>{m.noId}</Code>
              </Text>
              <Text size="sm" td="line-through" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
                {m.antes ?? '(vazio)'}
              </Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {m.depois ?? '(vazio)'}
              </Text>
            </Stack>
          )}
          {(m.tipo === 'no_criado' || m.tipo === 'no_desativado' || m.tipo === 'no_reativado') && (
            <Text size="sm">
              {m.tipo === 'no_criado'
                ? 'nó criado'
                : m.tipo === 'no_desativado'
                  ? 'nó desativado'
                  : 'nó reativado'}
              : <Code>{m.noId}</Code> — {m.detalhe}
            </Text>
          )}
          {(m.tipo === 'opcao_criada' || m.tipo === 'opcao_removida') && (
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                {m.tipo === 'opcao_criada' ? 'opção criada' : 'opção removida'} em{' '}
                <Code>{m.noId}</Code>
              </Text>
              <Text size="sm" td={m.tipo === 'opcao_removida' ? 'line-through' : undefined}>
                {m.label} · {m.destinoLegivel}
              </Text>
            </Stack>
          )}
          {m.tipo === 'destino_trocado' && (
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                destino de “{m.label}” em <Code>{m.noId}</Code>
              </Text>
              <Text size="sm" td="line-through" c="dimmed">
                {m.antes}
              </Text>
              <Text size="sm">{m.depois}</Text>
            </Stack>
          )}
          {m.tipo === 'opcao_movida' && (
            <Text size="sm">
              “{m.label}” saiu de <Code>{m.de}</Code> e foi para <Code>{m.para}</Code>
            </Text>
          )}
          {m.tipo === 'ordem' && (
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                ordem das opções em <Code>{m.noId}</Code>
              </Text>
              <Text size="xs" c="dimmed">
                antes: {m.antes.join(' · ')}
              </Text>
              <Text size="xs">depois: {m.depois.join(' · ')}</Text>
            </Stack>
          )}
        </Paper>
      ))}
    </Stack>
  );
}

function Historico({
  itens,
  podeRestaurar,
  temRascunho,
  salvando,
  aoRestaurar,
}: {
  itens: readonly VersaoNoHistorico[];
  podeRestaurar: boolean;
  temRascunho: boolean;
  salvando: boolean;
  aoRestaurar: (id: string) => void;
}): JSX.Element {
  if (itens.length === 0) {
    return (
      <Vazio titulo="Nenhuma versão no histórico.">
        A primeira versão aparece aqui depois da semeadura, e cada publicação acrescenta uma.
      </Vazio>
    );
  }

  return (
    <Table.ScrollContainer minWidth={620}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={70}>Versão</Table.Th>
            <Table.Th w={110}>Situação</Table.Th>
            <Table.Th>Nota</Table.Th>
            <Table.Th w={170}>Publicada</Table.Th>
            <Table.Th w={110} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {itens.map((v) => (
            <Table.Tr key={v.id}>
              <Table.Td>
                <Text size="sm" ff="monospace">
                  {v.numero}
                </Text>
              </Table.Td>
              <Table.Td>
                {/* Texto, não só cor: a situação é o que decide o que dá para
                    fazer com a linha. */}
                <Badge
                  color={
                    v.status === 'publicada' ? 'teal' : v.status === 'rascunho' ? 'orange' : 'gray'
                  }
                >
                  {v.status === 'publicada' ? 'no ar' : v.status}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{v.nota ?? '—'}</Text>
                <Text size="xs" c="dimmed">
                  {v.nos} nó(s) · {v.opcoes} opção(ões)
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="xs" c="dimmed">
                  {v.publicadaEm ? new Date(v.publicadaEm).toLocaleString('pt-BR') : '—'}
                  {v.publicadaPorNome ? ` · ${v.publicadaPorNome}` : ''}
                </Text>
              </Table.Td>
              <Table.Td>
                {podeRestaurar && v.status !== 'rascunho' && (
                  <Tooltip
                    label={
                      temRascunho
                        ? 'descarte ou publique o rascunho antes de restaurar'
                        : 'cria um rascunho com o conteúdo desta versão'
                    }
                  >
                    <Button
                      size="xs"
                      disabled={temRascunho}
                      loading={salvando}
                      onClick={() => aoRestaurar(v.id)}
                    >
                      Restaurar
                    </Button>
                  </Tooltip>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

// ---------------------------------------------------------------------------

/**
 * Um campo de texto do nó.
 *
 * Sob o campo fica SÓ a contagem de caracteres. A lista de marcadores é
 * informação do NÓ, não do campo — repetida sob cinco campos virava ruído e
 * empurrava a prévia para o fim da rolagem. Ela mora no cabeçalho do nó.
 */
function CampoDeTexto({
  rotulo,
  valor,
  editavel,
  salvando,
  aoSalvar,
}: {
  rotulo: string;
  valor: string;
  editavel: boolean;
  salvando: boolean;
  aoSalvar: (valor: string) => void;
}): JSX.Element {
  const [rascunho, setRascunho] = useState(valor);
  const mudou = rascunho !== valor;

  // O valor do servidor vence quando a árvore recarrega.
  useEffect(() => {
    setRascunho(valor);
  }, [valor]);

  return (
    <Stack gap={4}>
      <Textarea
        label={rotulo}
        value={rascunho}
        autosize
        minRows={2}
        maxRows={10}
        readOnly={!editavel}
        onChange={(e) => setRascunho(e.currentTarget.value)}
      />
      <Group gap="xs" justify="space-between" wrap="wrap">
        <Text size="xs" c="dimmed">
          {rascunho.length} caracteres
        </Text>
        {editavel && mudou && (
          <Group gap="xs">
            <Button size="xs" variant="subtle" onClick={() => setRascunho(valor)}>
              Desfazer
            </Button>
            <Button size="xs" loading={salvando} onClick={() => aoSalvar(rascunho)}>
              Salvar no rascunho
            </Button>
          </Group>
        )}
      </Group>
    </Stack>
  );
}

function LinhaDeOpcao({
  opcao,
  limite,
  editavel,
  salvando,
  primeira,
  ultima,
  aoSalvar,
  aoMover,
  aoTrocarDestino,
  aoTrocarVisibilidade,
  menuPorTipoAtivo = false,
  noDiferencia: diferencia = false,
  aoRemover,
  aoAbrirFaq,
  abrindoFaq,
}: {
  opcao: OpcaoDoMenu;
  limite: number;
  editavel: boolean;
  salvando: boolean;
  primeira: boolean;
  ultima: boolean;
  aoSalvar: (label: string) => void;
  aoMover: (direcao: 'cima' | 'baixo') => void;
  /** Ausentes quando a tela está lendo a versão publicada. */
  aoTrocarDestino?: () => void;
  aoTrocarVisibilidade?: () => void;
  /** Estado do interruptor, para o selo dizer se a regra vale hoje. */
  menuPorTipoAtivo?: boolean;
  /**
   * Alguma opção DESTE nó tem regra valendo?
   *
   * Decide se a ausência de regra ganha selo. Ver `noDiferencia`.
   */
  noDiferencia?: boolean;
  aoRemover?: () => void;
  aoAbrirFaq: (faqId: string) => void;
  abrindoFaq: boolean;
}): JSX.Element {
  const [rascunho, setRascunho] = useState(opcao.label);
  const mudou = rascunho !== opcao.label;
  const longo = rascunho.length > limite;

  useEffect(() => {
    setRascunho(opcao.label);
  }, [opcao.label]);

  return (
    <Paper withBorder p="xs" radius="sm">
      <Group gap="xs" align="flex-start" wrap="nowrap">
        {/* Número derivado da ordem — é o que o aluno digita. */}
        <Badge variant="light" miw={34}>
          {opcao.numero ?? '?'}
        </Badge>

        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <TextInput
            size="xs"
            value={rascunho}
            readOnly={!editavel}
            onChange={(e) => setRascunho(e.currentTarget.value)}
            error={longo ? `${rascunho.length} caracteres: quebra a linha no WhatsApp` : null}
          />
          <Group gap="xs" wrap="wrap">
            {/* Destino: texto, não só cor. É o que diz o que a opção FAZ. O
                selo traz a PERGUNTA da FAQ, não o slug — e a frase inteira no
                `title`, porque pergunta longa é a regra e não a exceção. */}
            <Badge
              size="xs"
              color={COR_DESTINO[opcao.tipoDeDestino]}
              variant="light"
              title={opcao.destinoLegivel}
              maw={360}
              styles={{ label: { overflow: 'hidden', textOverflow: 'ellipsis' } }}
            >
              {opcao.destinoLegivel}
            </Badge>
            <Text size="xs" c={longo ? 'orange' : 'dimmed'}>
              {rascunho.length}/{limite}
            </Text>
            {/* Quem vê. SÓ quando há regra: selo "todos" em sete de oito
                linhas seria ruído, e a informação aqui é a exceção.
                Marca + texto, nunca só a cor. */}
            {/* "todos" só onde há CONTRASTE: num nó em que nenhuma opção
                tem regra valendo, este selo em toda linha seria ruído e não
                haveria decisão a tomar. Onde alguma tem, a ausência passa a
                ser informação — e sem o selo ela se confundiria com "a tela
                não está mostrando". Mais apagado que os selos de regra, de
                propósito: é o estado padrão, não a exceção. */}
            {!opcao.visivelRegra && diferencia && (
              <Tooltip label="sem regra: todo mundo vê esta opção">
                <Badge size="xs" color="gray" variant="outline" opacity={0.6}>
                  {MARCA_TODOS} {ROTULO_TODOS}
                </Badge>
              </Tooltip>
            )}
            {opcao.visivelRegra && (
              <Tooltip
                label={
                  regraInerte(opcao.visivelRegra, menuPorTipoAtivo)
                    ? `${DESCRICAO_REGRA[opcao.visivelRegra] ?? opcao.visivelRegra} — mas o menu por tipo está DESLIGADO, então ela não esconde nada hoje`
                    : (DESCRICAO_REGRA[opcao.visivelRegra] ?? opcao.visivelRegra)
                }
                multiline
                w={280}
              >
                <Badge
                  size="xs"
                  color={regraInerte(opcao.visivelRegra, menuPorTipoAtivo) ? 'gray' : 'yellow'}
                  variant={regraInerte(opcao.visivelRegra, menuPorTipoAtivo) ? 'outline' : 'light'}
                >
                  {regraInerte(opcao.visivelRegra, menuPorTipoAtivo) ? '○ ' : ''}
                  {MARCA_REGRA[opcao.visivelRegra] ?? '•'}{' '}
                  {ROTULO_REGRA[opcao.visivelRegra] ?? opcao.visivelRegra.replace(/_/g, ' ')}
                </Badge>
              </Tooltip>
            )}
            {mudou && editavel && (
              <Button size="compact-xs" loading={salvando} onClick={() => aoSalvar(rascunho)}>
                Salvar
              </Button>
            )}
            {/* Os estados da FAQ que mudam o que o aluno recebe. Cada um com
                TEXTO, e o aviso aparece antes de a validação bloquear. */}
            {/*
              `?? []` de propósito: painel e API sobem como serviços separados,
              e numa janela de deploy o painel novo pode falar com a API
              anterior por um minuto. Campo ausente ali não pode virar tela
              branca — vira selo a menos.
            */}
            {(opcao.faqs ?? []).map((f) => (
              <Group gap={4} key={f.id ?? f.pergunta} wrap="nowrap">
                {/* Nos destinos de FAQ, a pergunta já é o `destinoLegivel`.
                    Numa AÇÃO o rótulo é a descrição da operação, então a
                    pergunta que ela envia precisa aparecer aqui — senão a
                    pessoa tem um "Ver resposta" sem saber de que FAQ. */}
                {opcao.tipoDeDestino === 'acao' && !f.ausente && (
                  <Badge
                    size="xs"
                    color="teal"
                    variant="light"
                    title={f.pergunta}
                    maw={360}
                    styles={{ label: { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                  >
                    {f.pergunta}
                  </Badge>
                )}
                {f.ausente && (
                  <Badge size="xs" color="red" variant="filled" title={f.pergunta}>
                    ⚠ FAQ não encontrada
                  </Badge>
                )}
                {!f.ausente && !f.ativo && (
                  <Badge size="xs" color="red" variant="filled">
                    ⚠ FAQ inativa — vai quebrar na publicação
                  </Badge>
                )}
                {f.reindexacaoPendente && (
                  <Badge size="xs" color="violet" variant="light">
                    aguarda reindexação (a busca livre não acha)
                  </Badge>
                )}
                {f.encaminhaParaSecretaria && (
                  <Badge size="xs" color="orange" variant="light">
                    na prática transborda: categoria de encaminhamento
                  </Badge>
                )}
                {f.id && (
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    loading={abrindoFaq}
                    onClick={() => aoAbrirFaq(f.id as string)}
                  >
                    Ver resposta
                  </Button>
                )}
              </Group>
            ))}

            {/* Ação em código: o que ela envia não está na árvore. Dizer isso
                aqui evita procurar o texto num campo que não existe — e deixa
                claro que editar o rótulo não muda o que o bot faz. */}
            {opcao.textoNoCodigo && (
              <Badge
                size="xs"
                color="gray"
                variant="outline"
                title={`Esta opção envia ${opcao.textoNoCodigo}. Esse texto está no código do bot, não na árvore: mudar o rótulo aqui não muda o que o aluno recebe.`}
                maw={360}
                styles={{ label: { overflow: 'hidden', textOverflow: 'ellipsis' } }}
              >
                🔒 só o rótulo é editável — envia {opcao.textoNoCodigo}
              </Badge>
            )}

            {/* Pergunta livre: o tamanho do que a categoria alcança. */}
            {opcao.categoria && (
              <Badge
                size="xs"
                color={opcao.categoria.faqsAtivas === 0 ? 'red' : 'violet'}
                variant={opcao.categoria.faqsAtivas === 0 ? 'filled' : 'light'}
              >
                {opcao.categoria.faqsAtivas === 0
                  ? '⚠ nenhuma FAQ ativa: toda pergunta aqui vira lacuna'
                  : `${opcao.categoria.faqsAtivas} FAQ(s) ativa(s) nesta categoria`}
              </Badge>
            )}

            {aoTrocarDestino && (
              <Button size="compact-xs" variant="subtle" onClick={aoTrocarDestino}>
                Trocar destino
              </Button>
            )}
            {aoTrocarVisibilidade && (
              <Button size="compact-xs" variant="subtle" onClick={aoTrocarVisibilidade}>
                Quem vê
              </Button>
            )}
            {aoRemover && (
              <Button
                size="compact-xs"
                variant="subtle"
                color="red"
                loading={salvando}
                onClick={aoRemover}
              >
                Remover
              </Button>
            )}
          </Group>
        </Stack>

        {editavel && (
          <Stack gap={2}>
            <ActionIcon
              size="sm"
              variant="default"
              aria-label="Subir opção"
              disabled={primeira || salvando}
              onClick={() => aoMover('cima')}
            >
              ↑
            </ActionIcon>
            <ActionIcon
              size="sm"
              variant="default"
              aria-label="Descer opção"
              disabled={ultima || salvando}
              onClick={() => aoMover('baixo')}
            >
              ↓
            </ActionIcon>
          </Stack>
        )}
      </Group>
    </Paper>
  );
}
